
import { defineComponent, computed } from 'vue';
import urlRegex from 'url-regex';
import { Message, MessagePart, Emote } from './types';

function extractEmotes({ emotes }: Message) {
  if (!emotes) {
    return [];
  }

  const extracted: Emote[] = [];
  const emoteGroups = emotes.split('/');

  for (let i = 0; i < emoteGroups.length; i++) {
    const [code, locations] = emoteGroups[i].split(':');
    const indexGroups = locations.split(',');

    for (let j = 0; j < indexGroups.length; j++) {
      const location = indexGroups[j].split('-').map(Number);
      extracted.push({
        code,
        location,
      });
    }
  }

  return extracted;
}

const urlRegexMatcher = urlRegex({ strict: false });

function textToPartsWithUrl(text: string): MessagePart[] {
  // TOOD: Fix matching !,. at the end of a url.
  const matches = text.matchAll(urlRegexMatcher);
  const arrMatches: RegExpMatchArray[] = [];

  for (const match of matches) {
    arrMatches.push(match);
  }

  const parts: MessagePart[] = [];

  for (let i = arrMatches.length - 1; i >= 0; i--) {
    const match = arrMatches[i];

    if (match.index !== undefined && match[0].length) {
      const endIndex = match.index + match[0].length;
      if (endIndex < text.length) {
        parts.unshift({
          type: 'span',
          text: text.slice(endIndex),
        });
      }

      parts.unshift({
        type: 'a',
        text: match[0],
        data: {
          target: '_blank',
          ref: 'noreferrer noopener',
          href: match[0],
        },
      });

      text = text.slice(0, match.index);
    }
  }

  if (text.length) {
    parts.unshift({
      type: 'span',
      text,
    });
  }

  return parts;
}

export default defineComponent({
  name: 'ChatMessage',
  props: {
    message: {
      type: Object as () => Message,
      required: true,
    },
    showChannel: {
      type: Boolean,
      default: false,
    },
  },
  emits: [
    'user-filter:update',
    'channel-filter:update',
  ],
  setup(props) {
    const messageParts = computed(() => {
      // Extract emotes and sort them in descending order of occurence.
      const emotes = extractEmotes(props.message).sort((a, b) => (
        b.location[0] - a.location[0]
      ));
      const partsArray: MessagePart[] = [];

      // Convert the string to an array to correctly handle emoji lengths.
      // '🐌'.length === 2, but [...'🐌'].length === 1.
      let message = [...props.message.message];

      for (let i = 0; i < emotes.length; i++) {
        const { code, location } = emotes[i];
        const [start, end] = location;

        const emoteText = message.slice(start, end + 1).join('');
        const text = message.slice(end + 1).join('');

        if (text.length) {
          const urlParts = textToPartsWithUrl(text);
          partsArray.unshift(...urlParts);
        }

        partsArray.unshift({
          type: 'img',
          tooltip: emoteText,
          data: {
            alt: emoteText,
            class: 'chat-line-emote',
            src: `https://static-cdn.jtvnw.net/emoticons/v2/${code}/default/dark/1.0`,
          },
        });

        message = message.slice(0, start);
      }

      if (message.length) {
        const urlParts = textToPartsWithUrl(message.join(''));
        partsArray.unshift(...urlParts);
      }

      return partsArray;
    });

    return {
      messageParts,
    };
  },
});
